import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Edit, Save } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, Container, Skeleton, Tooltip } from '@mui/material';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { fetchWidgetDetails, saveWidgetPlacement } from 'store/actions/widgetActions';
import MapChart from 'components/charts/map/MapChart';
import Charts from 'components/charts/Charts';
import { fetchDefaultCollection } from 'store/actions/collectionActions';

const ResponsiveGridLayout = WidthProvider(Responsive);

const SkeletonCard = () => (
  <Card sx={{ height: '100%', width: '400px', mr: 2 }}>
    <CardContent>
      <Skeleton variant='rectangular' height={400} />
    </CardContent>
  </Card>
);

const TopAlertBox = ({ handleCloseAlert, loading, showAlert }) => {
  if (loading || !showAlert) return null;
  return (
    <Alert severity='info' onClose={handleCloseAlert}>
      You can customize the layout by dragging and resizing the widgets. Simply click the button to
      make changes, and remember to save your layout by clicking the save button.
    </Alert>
  );
};

TopAlertBox.propTypes = {
  handleCloseAlert: PropTypes.func,
  loading: PropTypes.bool,
  showAlert: PropTypes.bool,
};

const LayoutEditButton = ({ loading, isDraggable, handleWidgetPlacement, toggleDraggable }) => {
  if (loading) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        mb: 2,
        position: 'fixed',
        zIndex: '1',
        right: '10px',
      }}
    >
      {isDraggable ? (
        <Tooltip title='Save Layout'>
          <Button variant='outlined' onClick={handleWidgetPlacement}>
            <Save />
          </Button>
        </Tooltip>
      ) : (
        <Tooltip title='Edit Layout'>
          <Button variant='outlined' onClick={toggleDraggable}>
            <Edit />
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

LayoutEditButton.propTypes = {
  handleWidgetPlacement: PropTypes.func,
  isDraggable: PropTypes.bool,
  loading: PropTypes.bool,
  toggleDraggable: PropTypes.func,
};

// const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const CollectionGrid = ({ publicView }) => {
  const dispatch = useDispatch();

  const store = useSelector((state: any) => state);
  const { widgets, widgetDetails } = store.widget;
  const { authInfo } = store.auth;

  const [isDraggable, setIsDraggable] = useState(false);
  const [loading, setLoading] = useState(true);
  const toggleDraggable = () => setIsDraggable(!isDraggable);
  const [widgetDetailsLoaded, setWidgetDetailsLoaded] = useState(false);
  const [layout, setLayout] = useState([]);
  const [showAlert, setShowAlert] = useState(true);

  useEffect(() => {
    if (authInfo.isAuthorized) {
      dispatch({ type: fetchDefaultCollection.request });
    }
  }, [dispatch, authInfo.isAuthorized]);

  useEffect(() => {
    if (widgets.length > 0) {
      setLoading(false);
    }
  }, [widgets, loading]);

  useEffect(() => {
    const fetchData = async () => {
      if (widgets.length > 0) {
        setLoading(true);
        for (const { id } of widgets) {
          dispatch({ type: fetchWidgetDetails.request, payload: { widgetId: id } });
          // await sleep(500);
        }
        setLoading(false); // Optionally stop loading after all dispatches
      }
    };
    fetchData(); // Call the async function
  }, [widgets, dispatch]);

  useEffect(() => {
    if (widgets.length > 0 && Object.keys(widgetDetails).length > 0) {
      setLoading(false);
      setWidgetDetailsLoaded(true);
    }
  }, [widgets, widgetDetails]);

  const handleWidgetPlacement = () => {
    dispatch({
      type: saveWidgetPlacement.request,
      payload: {
        widgets: layout,
      },
    });
    setIsDraggable(!isDraggable);
  };

  const onLayoutChange = (layout) => {
    const newLayout = layout.map((item) => {
      const widgetId = item.i.split('-')[1];
      return {
        id: parseInt(widgetId),
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        static: item.static,
      };
    });
    setLayout(newLayout);
  };

  const getLayout = () => {
    return {
      lg: widgets.map(({ id, configurations }) => ({
        w: configurations.w || 6,
        h: configurations.h || 2,
        x: configurations.x || 0,
        y: configurations.y || 0,
        i: `chart-${id}`,
      })),
    };
  };

  const getDynamicStyle = () => ({
    border: isDraggable ? '2px dashed #4FC3F7' : 'none',
    cursor: isDraggable ? 'grab' : 'default',
  });

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const calculateBreakpoints = () => {
    const baseBreakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
    const baseCols = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };

    const containerWidth = window.innerWidth;

    // This handles the case with mobile devices
    if (containerWidth < 500) {
      return baseBreakpoints;
    }

    const breakpoints = {};
    Object.entries(baseBreakpoints).forEach(([key]) => {
      breakpoints[key] = Math.ceil(containerWidth / (1200 / baseCols[key]));
    });
    return breakpoints;
  };

  return (
    <Box
      component='main'
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth='xl'>
        {!publicView && (
          <TopAlertBox
            handleCloseAlert={handleCloseAlert}
            loading={loading}
            showAlert={showAlert}
          />
        )}
        {!publicView && window.innerWidth > 700 && (
          <LayoutEditButton
            loading={loading}
            isDraggable={isDraggable}
            handleWidgetPlacement={handleWidgetPlacement}
            toggleDraggable={toggleDraggable}
          />
        )}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </Box>
        ) : (
          widgetDetailsLoaded && (
            <ResponsiveGridLayout
              className='layout'
              layouts={getLayout()}
              breakpoints={calculateBreakpoints()}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={100}
              autoSize={true}
              isResizable={isDraggable}
              isDraggable={isDraggable}
              onLayoutChange={(layout) => onLayoutChange(layout)}
              onBreakpointChange={(newBreakpoint, newCols) => {
                console.log('BreakPoint Change -', newBreakpoint, newCols);
              }}
            >
              {Object.values(widgetDetails).map((widget: any) => (
                <div key={`chart-${widget.id}`} style={getDynamicStyle()}>
                  <Card variant='outlined' sx={{ height: '100%' }}>
                    <CardContent sx={{ height: '100%' }}>
                      {widget?.charts?.[0]?.type === 'map' ? (
                        <MapChart
                          key={widget?.charts?.[0]?.name}
                          title={widget?.title}
                          charts={widget?.charts?.[0]}
                        />
                      ) : (
                        <Charts
                          key={`chart-${widget.charts?.[0]?.name}`}
                          series={widget?.charts}
                          type={widget?.charts?.[0]?.type}
                          titleText={widget.title}
                          isDraggable={isDraggable}
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </ResponsiveGridLayout>
          )
        )}
      </Container>
    </Box>
  );
};

CollectionGrid.propTypes = {
  publicView: PropTypes.bool,
};

export default CollectionGrid;
